import React, { useCallback } from 'react';
import { Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { useIntl } from 'react-intl';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import TrendingFlatOutlinedIcon from '@mui/icons-material/TrendingFlatOutlined';
import { useMessages } from 'modules/intl';
import { TimeSignalAcceptedBy, TimeSignalDimensionType } from 'graphql/types';
import { useGetTimeSignalAnalyticsData } from 'modules/Insights/Notes/hooks/useGetTimeSignalAnalyticsData';
import { useGetPreviousAnalyticsData } from 'modules/Insights/Notes/hooks/useGetPreviousAnalyticsData';
import { AutoAcceptComponent } from './AutoAcceptComponent';
import { transformAnalyticsData } from './utils';
import { AutoAcceptSkeleton } from './AutoAcceptSkeleton';
import { AutoAcceptNoData } from './AutoAcceptNoData';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0, 2),
    marginRight: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    minWidth: '42%',
    justifyContent: 'center'
  },
  acceptContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(1)
  },
  percentage: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: theme.spacing(2.5)
  },
  div: {
    display: 'flex',
    flexDirection: 'row'
  },
  span: {
    alignSelf: 'center',
    margin: theme.spacing(0, 3),
    color: theme.palette.text.secondary,
    fontSize: theme.spacing(1.8)
  },
  icon: {
    color: theme.palette.success.main,
    marginLeft: theme.spacing(1)
  }
}));

const labels = ['insights.autoAccept.SYSTEM', 'insights.autoAccept.USER'];
export const AutoAccept: React.FC = () => {
  const { data, loading, isEmpty } = useGetTimeSignalAnalyticsData(
    TimeSignalDimensionType.MetricsByAutoaccept
  );
  const {
    data: previousData,
    loading: previousLoading
  } = useGetPreviousAnalyticsData(TimeSignalDimensionType.MetricsByAutoaccept);

  const tranformData = transformAnalyticsData(data?.timeSignalAnalytics?.items);
  const prevTransformData = transformAnalyticsData(
    previousData?.timeSignalAnalytics?.items
  );
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const messages = useMessages(labels);

  const displayTrends = useCallback(
    (label: string) => {
      if (tranformData[label] > prevTransformData[label]) {
        return (
          <TrendingUpOutlinedIcon
            className={classes.icon}
            data-testid={`upOutlinedIcon-${label}`}
          />
        );
      } else if (tranformData[label] < prevTransformData[label]) {
        return (
          <TrendingDownOutlinedIcon
            className={classes.icon}
            data-testid={`downOutlinedIcon-${label}`}
          />
        );
      } else
        return (
          <TrendingFlatOutlinedIcon
            className={classes.icon}
            data-testid={`flatOutlinedIcon-${label}`}
          />
        );
    },
    [classes.icon, prevTransformData, tranformData]
  );

  return (
    <>
      {loading || previousLoading ? (
        <AutoAcceptSkeleton />
      ) : isEmpty ? (
        <AutoAcceptNoData />
      ) : (
        <Paper
          className={classes.container}
          elevation={3}
          data-testid={'auto-accept-container'}
        >
          <div className={classes.div}>
            <AutoAcceptComponent
              label={messages['insights.autoAccept.SYSTEM']}
              element={
                <Typography
                  variant="h4"
                  className={classes.percentage}
                  data-testid="auto-accept-percentage-system"
                >
                  {`${tranformData[TimeSignalAcceptedBy.System]}%`}
                </Typography>
              }
              trendsElement={displayTrends(TimeSignalAcceptedBy.System)}
            />
            <Typography
              variant="caption"
              whiteSpace="nowrap"
              className={classes.span}
              data-testid="auto-accept-percentage-system-message"
            >
              {formatMessage({ id: 'insights.autoAccept.vs' })}
            </Typography>
            <AutoAcceptComponent
              label={messages['insights.autoAccept.USER']}
              element={
                <Typography
                  variant="h4"
                  className={classes.percentage}
                  data-testid="auto-accept-percentage-user"
                >
                  {`${tranformData[TimeSignalAcceptedBy.User]}%`}
                </Typography>
              }
              trendsElement={displayTrends(TimeSignalAcceptedBy.User)}
            />
          </div>
        </Paper>
      )}
    </>
  );
};
